import {useEffect, useRef, useState} from 'react'

export type DefaultPreventedDropTargetRef = {
  readonly current: Node | null
}

export interface UseDragDropOptions {
  onFilesDrop: (files: FileList) => void
  targetElement?: EventTarget | null
  defaultPreventedDropTargetRef?: DefaultPreventedDropTargetRef
}

export function useDragDrop({onFilesDrop, targetElement, defaultPreventedDropTargetRef}: UseDragDropOptions) {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  useEffect(() => {
    if (!targetElement) {
      return
    }

    const resetDragState = () => {
      dragCounterRef.current = 0
      setIsDragging(false)
    }

    const shouldIgnoreDefaultPreventedEvent = (event: Event) => {
      // Only reconsider events another handler already claimed via preventDefault().
      if (!event.defaultPrevented) return false

      // The chat's own drop target (e.g. the composer form) is always chat's to handle.
      if (isEventFromDropTarget(event, defaultPreventedDropTargetRef?.current ?? null)) return false

      // Otherwise only yield to a genuine external text editor that claimed the drop
      // (e.g. the draft-issue description field, which is a <textarea>). Every other
      // target — empty space, the conversation area, non-editor drop zones — still
      // attaches to chat, so dropping an image anywhere on screen adds it to the message.
      return isEventFromTextEditor(event)
    }

    const handleDragOver = (event: Event) => {
      if (shouldIgnoreDefaultPreventedEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    }

    const handleDragEnter = (event: Event) => {
      if (shouldIgnoreDefaultPreventedEvent(event) || !isDragEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      // Only handle file drag-and-drops.
      if (event.dataTransfer?.types.includes('Files')) {
        dragCounterRef.current++
        if (dragCounterRef.current === 1) {
          setIsDragging(true)
        }
      }
    }

    const handleDragLeave = (event: Event) => {
      if (shouldIgnoreDefaultPreventedEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      dragCounterRef.current--
      if (dragCounterRef.current <= 0) {
        resetDragState()
      }
    }

    const handleDrop = (event: Event) => {
      if (!isDragEvent(event)) {
        return
      }

      if (shouldIgnoreDefaultPreventedEvent(event)) {
        resetDragState()
        return
      }

      event.preventDefault()
      event.stopPropagation()

      resetDragState()

      const files = event.dataTransfer?.files
      if (!files || files.length === 0) return

      onFilesDrop(files)
    }

    targetElement.addEventListener('dragover', handleDragOver)
    targetElement.addEventListener('dragenter', handleDragEnter)
    targetElement.addEventListener('dragleave', handleDragLeave)
    targetElement.addEventListener('drop', handleDrop)

    return () => {
      targetElement.removeEventListener('dragover', handleDragOver)
      targetElement.removeEventListener('dragenter', handleDragEnter)
      targetElement.removeEventListener('dragleave', handleDragLeave)
      targetElement.removeEventListener('drop', handleDrop)
    }
  }, [onFilesDrop, targetElement, defaultPreventedDropTargetRef])

  return {isDragging}
}

function isDragEvent(event: Event): event is DragEvent {
  return event instanceof DragEvent
}

function isEventFromDropTarget(event: Event, dropTarget: Node | null) {
  if (!dropTarget) return false

  if (event.target instanceof Node && dropTarget.contains(event.target)) return true

  return event.composedPath().includes(dropTarget)
}

function isEventFromTextEditor(event: Event) {
  if (event.target instanceof Element && event.target.closest('textarea')) return true

  return event.composedPath().some(node => node instanceof HTMLTextAreaElement)
}
